// src/components/Shared/FigmaIcons.jsx
// Eventory icon set — backed by @phosphor-icons/react.
//
// Every icon exported here takes a `className` prop (kept for backwards
// compatibility with the existing call sites that style icons via the
// `.ev-icon-svg` rule). Under the hood, each is a curated Phosphor icon at
// the brand's chosen visual weight. Switching the whole set's weight (e.g.
// from `regular` to `bold` or `fill`) is a one-line change in this file.

import React from 'react';
import {
	Bell,
	Plus,
	ArrowLeft,
	ArrowsClockwise,
	House,
	SquaresFour,
	Hamburger,
	ListBullets,
	ForkKnife,
	UsersThree,
	MapPin,
	Users,
	Sparkle,
	HandbagSimple,
	CaretRight,
	Eye,
	EyeSlash,
	Clock,
	ChefHat,
	CheckCircle,
	BowlFood,
	LightningSlash,
	ShoppingCart,
	Timer,
	ChartBar,
	QrCode,
	XCircle,
	Note,
	Warning,
	EnvelopeSimple,
	Ticket,
	WifiHigh,
	WifiSlash,
	Confetti,
	DeviceMobile,
	SlidersHorizontal,
	MonitorPlay,
	CookingPot,
	Stack,
} from '@phosphor-icons/react';

// Brand-wide icon weight. `regular` matches the editorial / premium feel of
// the marketing pages. Swap to `bold` or `fill` here to re-tone the whole app.
const WEIGHT = 'regular';

// Phosphor renders an SVG with width/height props. When the caller passes
// `className="ev-icon-svg"` (which has width/height in CSS) the CSS wins, so
// passing size doesn't fight existing sizing.
const wrap = (PhIcon) => ({ className, ...rest }) => (
	<PhIcon className={className} weight={WEIGHT} {...rest} />
);

/* ---- Foundational ---- */
export const IconBell = wrap(Bell);
export const IconPlus = wrap(Plus);
export const IconArrowLeft = wrap(ArrowLeft);
export const IconRefresh = wrap(ArrowsClockwise);
export const IconChevronRight = wrap(CaretRight);
export const IconEye = wrap(Eye);
export const IconEyeOff = wrap(EyeSlash);

/* ---- Navigation & sections ---- */
export const IconHome = wrap(House);
export const IconOverview = wrap(SquaresFour);
export const IconKitchen = wrap(Hamburger);
export const IconCourse = wrap(ListBullets);
export const IconMeals = wrap(ForkKnife);
export const IconUsers = wrap(Users);
export const IconGuests = wrap(UsersThree);
export const IconLocationPin = wrap(MapPin);

/* ---- Admin nav additions (Setup / Station Monitor / Service / Templates) ---- */
export const IconSettings = wrap(SlidersHorizontal);
export const IconMonitor = wrap(MonitorPlay);
export const IconService = wrap(CookingPot);
export const IconStack = wrap(Stack);

/* ---- Hero / accent ---- */
export const IconSparkles = wrap(Sparkle);
export const IconBag = wrap(HandbagSimple);

/* ---- Order status (flow icons) ---- */
export const IconClock = wrap(Clock);
export const IconChef = wrap(ChefHat);
export const IconCheck = wrap(CheckCircle);
export const IconPlate = wrap(BowlFood);
export const IconBoltOff = wrap(LightningSlash);

/* ---- Customer + commerce ---- */
export const IconCart = wrap(ShoppingCart);
export const IconStopwatch = wrap(Timer);
export const IconChart = wrap(ChartBar);
export const IconQr = wrap(QrCode);

/* ---- Feedback (modals, banners, empty states) ---- */
export const IconX = wrap(XCircle);
export const IconNote = wrap(Note);
export const IconAlertTriangle = wrap(Warning);
export const IconEnvelope = wrap(EnvelopeSimple);
export const IconTicket = wrap(Ticket);

/* ---- Devices ---- */
export const IconPhone = wrap(DeviceMobile);

/* ---- Connectivity ---- */
export const IconWifi = wrap(WifiHigh);
export const IconWifiOff = wrap(WifiSlash);
export const IconSync = wrap(ArrowsClockwise);

/* ---- Delight ---- */
export const IconCelebrate = wrap(Confetti);
